import React from 'react';
import { usePublicReleaseNotes, IReleaseNote } from '../../services';
import { Button, Spinner, Text } from '../../components/UI';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ReleaseNoteEntry } from './ReleaseNoteEntry.component';

const LAST_SEEN_VERSION_KEY = 'last-seen-release-version';
const PAGE_SIZE = 20;

// #region WhatsNewTimeline
export const WhatsNewTimeline: React.FunctionComponent = (): React.ReactElement => {
    const [notes, loading, { listPublic, getLatestVersion }] = usePublicReleaseNotes();
    const [expandedOld, setExpandedOld] = React.useState<Set<number>>(new Set());
    const [offset, setOffset] = React.useState(0);
    const [hasMore, setHasMore] = React.useState(true);
    const [lastSeenVersion] = React.useState(() => localStorage.getItem(LAST_SEEN_VERSION_KEY) || '');

    React.useEffect(() => {
        loadInitial();
    }, []);

    // #region loadInitial
    const loadInitial = async () => {
        const results = await listPublic(PAGE_SIZE, 0);
        setOffset(PAGE_SIZE);
        setHasMore(results.length === PAGE_SIZE);

        const version = await getLatestVersion();
        if (version) {
            try { localStorage.setItem(LAST_SEEN_VERSION_KEY, version); } catch { /* ignore */ }
        }
    };

    // #region loadMore
    const loadMore = async () => {
        const results = await listPublic(PAGE_SIZE, offset);
        setOffset((prev) => prev + PAGE_SIZE);
        setHasMore(results.length === PAGE_SIZE);
    };

    // #region toggleOldExpand
    const toggleOldExpand = (id: number) => {
        setExpandedOld((prev) => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); } else { next.add(id); }
            return next;
        });
    };

    // #region isNewRelease
    const isNewRelease = (note: IReleaseNote): boolean => {
        if (!lastSeenVersion) return true;
        return note.version > lastSeenVersion;
    };

    if (loading && notes.length === 0) {
        return (
            <div className="flex justify-center py-12">
                <Spinner show={true} />
            </div>
        );
    }

    if (notes.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-y-3">
                <Text className="text-gray-500 dark:text-gray-400 text-lg">No release notes yet</Text>
                <Text className="text-gray-400 dark:text-gray-500 text-sm">Check back soon for updates</Text>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-y-0 py-4 max-w-3xl mx-auto">
            {notes.map((note: IReleaseNote, index: number) => {
                const isNew = isNewRelease(note);
                const isExpanded = isNew || expandedOld.has(note.id as number);

                return (
                    <ReleaseNoteEntry
                        key={note.id}
                        note={note}
                        isNew={isNew}
                        isExpanded={isExpanded}
                        onToggleExpand={() => toggleOldExpand(note.id as number)}
                        showConnector={index < notes.length - 1}
                    />
                );
            })}

            {hasMore && (
                <div className="flex justify-center pt-4">
                    <Button
                        color="zinc"
                        onClick={loadMore}
                        disabled={loading}
                    >
                        {loading ? <Spinner show={true} size={16} /> : <ArrowPathIcon data-slot="icon" />}
                        Load more
                    </Button>
                </div>
            )}
        </div>
    );
};
